import {Button} from "@luciad/ria-sample-common/components/button/Button";
import {ButtonGroup} from "@luciad/ria-sample-common/components/ButtonGroup";
import {HereMapsAPIKeyPrompt} from "@luciad/ria-sample-common/components/HereMapsAPIPrompt";
import {Modal} from "@luciad/ria-sample-common/components/Modal";
import {useHereMapsAPIKeyFromLocalStorage} from "@luciad/ria-sample-common/hooks/useHereMapsAPIKey";
import {useMapContext} from "@luciad/ria-sample-common/hooks/useMapContext";
import {useToastContext} from "@luciad/ria-sample-common/hooks/useToast";
import React from "react";
import {addLayer} from "../DataFormatsUtil";
import {HereMapsDataLoader, HereMapsType} from "../loaders/HereMapsDataLoader";

interface HereMapsButton {
  text: string;
  hereType: HereMapsType;
}

const HERE_MAPS_BUTTONS: HereMapsButton[] = [
  {
    text: "HERE Maps Aerial",
    hereType: "aerial"
  },
  {
    text: "HERE Maps Labeled Aerial",
    hereType: "aerialWithLabels"
  },
  {
    text: "HERE Maps Roads",
    hereType: "roads"
  },
  {
    text: "HERE Maps Traffic",
    hereType: "roadsWithTraffic"
  }
];

interface Props {
  show: boolean;
  onHide: () => void;
}

export const AddHereMapsModal = ({show, onHide}: Props) => {
  const {apiKey, setApiKey, validationState, validationFeedback} = useHereMapsAPIKeyFromLocalStorage();
  const {map} = useMapContext();
  const {showErrorToast} = useToastContext();

  const addHereMap = (type: HereMapsType) => {
    if (apiKey) {
      try {
        const hereLayer = HereMapsDataLoader.createLayer(type, apiKey);
        const existingHereLayer = map.layerTree.findLayerById("HERE");
        if (existingHereLayer) {
          map.layerTree.removeChild(existingHereLayer);
        }
        addLayer(map, hereLayer).then(layer => {
          // make sure text is legible by enabling scale snapping
          const oldSnapValue = map.mapNavigator.defaults.snapToScaleLevels;
          map.mapNavigator.defaults.snapToScaleLevels = true;
          map.layerTree.on("NodeRemoved", function(event) {
            if (event.node === layer) {
              map.mapNavigator.defaults.snapToScaleLevels = oldSnapValue;
            }
          });
        }).catch(error => {
          showErrorToast(error.message, error);
        });
      } catch (error) {
        showErrorToast(error.message, error);
      } finally {
        onHide();
      }
    }
  }

  const disabled = validationState !== 'valid';

  return (
      <Modal title="Add HERE Maps" show={show} onHide={onHide}>
        <ButtonGroup vertical={true}>
          {HERE_MAPS_BUTTONS.map(btn => {
            return (
                <Button
                    disabled={disabled}
                    title={disabled ? "An API key must be set before adding layers." : btn.text}
                    key={btn.text}
                    onClick={() => addHereMap(btn.hereType)}
                >
                  {btn.text}
                </Button>);
          })}
        </ButtonGroup>
        <HereMapsAPIKeyPrompt
            value={apiKey}
            onChange={setApiKey}
            validationState={validationState}
            validationFeedback={validationFeedback}
        />
      </Modal>
  )
}